/**
 * The assistant module's data layer. Everything goes through `api` (src/lib/api.ts) —
 * no raw fetch anywhere, so a failure arrives as an ApiRequestError the UI can read
 * and turn into an honest message rather than a crash.
 */
import { api, ApiRequestError } from '../../lib/api';
import type { AiConfig, AiReply } from '../../shared/types';

/**
 * Result of a verse search. There is no shared type for this yet — the module cannot
 * edit src/shared/types.ts, so this local copy matches the Worker's shape exactly.
 * (Reported in `needs` so it can move to shared later.)
 */
export interface VerseSuggestion {
  reference: string;
  why: string;
}

export const getConfig = () => api.get<AiConfig>('/assistant/config');
export const ask = (prompt: string) => api.post<AiReply>('/assistant/ask', { prompt });
export const searchVerse = (query: string) =>
  api.post<VerseSuggestion>('/assistant/verse-search', { query });

/**
 * The API's own words when it has them. Never invents a cause — an unreachable API is
 * not "you're not allowed", and a 500 is not "check your wifi".
 */
export function explain(err: unknown, fallback: string): string {
  if (err instanceof ApiRequestError) {
    if (err.body.error === 'api_unreachable')
      return 'The app loaded, but the API is not answering on this address.';
    if (err.body.detail) return err.body.detail;
    if (err.status === 403) return 'You are not allowed to do that.';
    if (err.status >= 500) return 'The assistant is up but hit an error. Try again in a moment.';
    return fallback;
  }
  if (err instanceof TypeError) return 'Could not reach the app. Check your connection.';
  return fallback;
}
